import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, SpecraDataTable, SpecraInput, TableHead, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { KpiInfo } from '../../../../components/delivery/widgets/KpiTile';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { applyTableView } from '../lib/tableSortFilter';
import { EmptyState, ErrorState } from './PageStates';
// Sentinel value for the "All" option in a select filter — Radix Select forbids
// empty-string item values, so "clear this filter" is modeled as a real option
// and mapped back to '' on change.
const ALL_FILTER = '__all__';
export function EntityTable({ data, columns, testid, rowTestid, rowKey, onRowClick, getRowLabel, rowActions, isLoading, emptyState, error, onRetry, hideRowActions, resource, sort, onToggleSort, filters, onFilterChange }) {
    const sortingEnabled = !!onToggleSort;
    const filteringEnabled = !!onFilterChange;
    const showFilterRow = filteringEnabled && columns.some((c)=>c.filter);
    const showActionsColumn = !!rowActions && !hideRowActions;
    // Filter + sort happen here (not at the call site) so every adopter gets the
    // same contract for free. No-op when neither feature is wired — returns the
    // original array reference.
    const displayData = useMemo(()=>applyTableView(data, columns, sort ?? null, filters ?? {}), [
        data,
        columns,
        sort,
        filters
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "space-y-2",
            "data-testid": Testids.deliveryShared.loadingSkeleton,
            children: [
                0,
                1,
                2
            ].map((i)=>/*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-12 w-full"
                }, i))
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(ErrorState, {
            error: error,
            title: "Failed to load",
            onRetry: onRetry
        });
    }
    if (data.length === 0) {
        // Never render a headers-only table: with no caller-provided empty state,
        // fall back to a generic one so the page always reaches a terminal state.
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: emptyState ?? /*#__PURE__*/ _jsx(EmptyState, {
                title: "No records yet"
            })
        });
    }
    // Filters can exclude every row while the underlying data is non-empty —
    // distinct from "no records", so keep the headers + filter row visible.
    const noMatches = displayData.length === 0;
    // Map the domain ColumnConfig onto the Specra design-system table. The
    // masking wrapper and value derivation stay HERE (domain concerns); the
    // chassis — chrome, a11y row semantics, keyboard nav — is the library's.
    const specraColumns = columns.map((col)=>({
            id: col.key,
            header: col.header,
            headerAdornment: col.headerInfo ? /*#__PURE__*/ _jsx(KpiInfo, {
                label: col.header,
                tip: col.headerInfo,
                testid: Testids.deliveryShared.infoTip,
                dataName: col.key
            }) : undefined,
            sortable: sortingEnabled && col.sortable,
            headerStyle: {
                width: col.width,
                textAlign: col.align ?? 'left'
            },
            style: {
                textAlign: col.align ?? 'left'
            },
            cell: (row)=>{
                const value = col.render ? col.render(row) : row[col.key];
                return col.maskAs && resource ? /*#__PURE__*/ _jsx(MaskedField, {
                    fieldName: col.maskAs,
                    resource: resource,
                    children: value
                }) : value;
            }
        }));
    if (showActionsColumn) {
        specraColumns.push({
            id: '__actions',
            header: '',
            headerStyle: {
                width: 80
            },
            style: {
                textAlign: 'right'
            },
            // Clicking the actions cell (including its whitespace) must never
            // trigger row navigation — same contract as before the Specra swap.
            cell: (row)=>/*#__PURE__*/ _jsx("div", {
                    onClick: (e)=>e.stopPropagation(),
                    children: rowActions?.(row)
                })
        });
    }
    const filterRow = showFilterRow ? /*#__PURE__*/ _jsxs(TableRow, {
        children: [
            columns.map((col)=>/*#__PURE__*/ _jsxs(TableHead, {
                    style: {
                        width: col.width
                    },
                    children: [
                        col.filter === 'text' && /*#__PURE__*/ _jsx(SpecraInput, {
                            value: filters?.[col.key] ?? '',
                            onChange: (e)=>onFilterChange?.(col.key, e.target.value),
                            placeholder: `Filter ${col.header}`,
                            "data-testid": Testids.dataTable.filterControl,
                            "data-col": col.key,
                            className: "h-8"
                        }),
                        col.filter === 'select' && /*#__PURE__*/ _jsxs(Select, {
                            value: filters?.[col.key] || ALL_FILTER,
                            onValueChange: (v)=>onFilterChange?.(col.key, v === ALL_FILTER ? '' : v),
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    className: "h-8",
                                    "data-testid": Testids.dataTable.filterControl,
                                    "data-col": col.key,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {})
                                }),
                                /*#__PURE__*/ _jsxs(SelectContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectItem, {
                                            value: ALL_FILTER,
                                            children: "All"
                                        }),
                                        col.filterOptions?.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    ]
                                })
                            ]
                        })
                    ]
                }, col.key)),
            showActionsColumn && /*#__PURE__*/ _jsx(TableHead, {
                style: {
                    width: 80
                }
            })
        ]
    }) : undefined;
    // A clickable row needs an accessible label; default to the first column's
    // underlying identity value (plain even when the column formats it).
    const firstColKey = columns[0]?.key;
    const rowLabel = getRowLabel ?? ((row)=>{
        if (!firstColKey) return 'row';
        const raw = row[firstColKey];
        return typeof raw === 'string' || typeof raw === 'number' ? String(raw) : 'row';
    });
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.pageState.content,
        children: /*#__PURE__*/ _jsx(SpecraDataTable, {
            columns: specraColumns,
            rows: displayData,
            getRowId: (row)=>rowKey(row),
            getRowLabel: onRowClick ? rowLabel : undefined,
            testId: testid,
            rowTestId: rowTestid,
            sortButtonTestId: Testids.dataTable.sortHeader,
            onRowClick: onRowClick,
            sortColumn: sort?.key,
            sortDirection: sort?.dir === 'desc' ? 'descending' : 'ascending',
            onSortChange: sortingEnabled ? (columnId)=>onToggleSort?.(columnId) : undefined,
            filterRow: filterRow,
            empty: noMatches ? /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-8 text-center text-sm text-muted-foreground",
                "data-testid": Testids.dataTable.noMatches,
                children: "No rows match the current filters."
            }) : undefined
        })
    });
}
